import BaseSearchController from "./base_search_controller"

const PRODUCT_PER_PAGE = 20

// Visual defaults for the caterer search map (see fitBounds below).
//   MIN_BOUNDS_PADDING_DEG — minimum half-span (in degrees of lat/lng) used
//     when all caterer pins cluster tightly around the search point. Without
//     this floor, the symmetric bounds collapse to a single point and
//     fitBounds zooms to ~21 (street-level on one block). 0.015° ≈ 1.7 km
//     of latitude in metropolitan France.
//   NO_PINS_DEFAULT_ZOOM — zoom level applied when the user's address yields
//     zero caterer matches (we still center the map on their address so they
//     see WHERE they searched). 11 ≈ city scale.
const MIN_BOUNDS_PADDING_DEG = 0.015
const NO_PINS_DEFAULT_ZOOM = 11

// Build a LatLngBounds rectangle centered EXACTLY on `center` whose half-span
// is large enough to enclose every point in `points`. Half-span is floored
// to `minPaddingDeg` so tightly-clustered points don't collapse the
// rectangle to zero span (which would make fitBounds zoom to ~21).
//
// Unlike repeated LatLngBounds.extend() calls — which produce a rectangle
// centered on the centroid of the input points — this returns a rectangle
// whose centroid is `center` by construction. Passing it to map.fitBounds()
// therefore places `center` at the visual center of the viewport, not on
// the centroid of the pins.
const symmetricBoundsAround = (center, points, minPaddingDeg) => {
  const latDelta = Math.max(minPaddingDeg, ...points.map((p) => Math.abs(p.lat - center.lat)))
  const lngDelta = Math.max(minPaddingDeg, ...points.map((p) => Math.abs(p.lng - center.lng)))

  return new google.maps.LatLngBounds(
    { lat: center.lat - latDelta, lng: center.lng - lngDelta },
    { lat: center.lat + latDelta, lng: center.lng + lngDelta },
  )
}

export default class extends BaseSearchController {
  static targets = [
    "centerPoint",
    "form",
    "map",
    "productInfoWindow",
    "productList",
    "productListItem",
    "results",
    "queryInfo",
    "noResultsMessage",
    "modalBody",
    "mobileFilterButton",
  ]

  async resultsTargetConnected() {
    this.scrollResultsToTop()
  }

  // Override base to pre-clean stale markers before super's count check.
  // turbo_stream.replace removes the old productInfoWindow elements but their
  // disconnect hooks (`productInfoWindowTargetDisconnected`) await
  // `google.maps.importLibrary("marker")` and can complete AFTER the new
  // connects. As a result, `this.markers` keeps the old entries inflated and
  // super's `markers.length == productInfoWindowTargets.length` check never
  // matches — so `fitBounds()` never gets called on a search update.
  async productInfoWindowTargetConnected(target) {
    this.markers = this.markers.filter(([marker, t]) => {
      if (document.contains(t)) return true

      marker.setMap(null)
      return false
    })

    await super.productInfoWindowTargetConnected(target)
  }

  // Override base to tolerate the case where our pre-prune in
  // productInfoWindowTargetConnected has already removed this target's marker.
  // Base's version throws `Cannot read properties of undefined (reading '0')`
  // when `find` returns undefined — silent in production but pollutes the
  // console and may interfere with Stimulus's internal lifecycle processing.
  async productInfoWindowTargetDisconnected(target) {
    await google.maps.importLibrary("marker")
    const markerContainer = this.markers.find((e) => e[1] == target)
    if (!markerContainer) return

    const [marker] = markerContainer
    marker.setMap(null)
    this.markers.splice(this.markers.indexOf(markerContainer), 1)
  }

  // Width 320px centered + 22px above the marker.
  infoBoxPixelOffset() {
    return new google.maps.Size(-320 / 2, -22)
  }

  // Override base. Caterer search filters by delivery coverage of a SINGLE
  // delivery point, not by map viewport — so the camera contract differs
  // from venue/activity_search:
  //
  //   1. The user's delivery point (queryInfoTarget) is ALWAYS the visual
  //      center of the map. The point comes from the typed address on a
  //      normal search, OR from the new map center after a manual pan
  //      (the server resolves queryInfo.lat/lng to @search.boundary.center
  //      whenever moved_map=true — see index.turbo_stream.erb).
  //   2. Zoom is fully automatic, derived from the spread of the pins
  //      around the user. No google_place_type cap (irrelevant — we're
  //      not zooming to a query granularity, we're framing a delivery
  //      radius). No hardcoded ceiling.
  //
  // Implementation: see symmetricBoundsAround() at the top of the file for
  // the geometric trick that makes "centered on the user AND fitting all
  // pins" expressible as a single fitBounds() call.
  async fitBounds() {
    // Defensive: drop markers whose DOM anchor has been removed but whose
    // disconnect hook hasn't completed yet (the async importLibrary await in
    // productInfoWindowTargetDisconnected can race with the next connect).
    this.markers = this.markers.filter(([marker, target]) => {
      if (document.contains(target)) return true

      marker.setMap(null)
      return false
    })

    const searchPoint = this.searchPoint()
    const pinCoords = this.productInfoWindowTargets.map((t) => ({ lat: parseFloat(t.dataset.lat), lng: parseFloat(t.dataset.lng)}))

    // Zero-result branch: no pins to frame, but we still center on the
    // user's address (when known) so they see WHERE they searched, even
    // if no caterer delivers there. Falling through to the symmetric-
    // bounds path would crash on Math.max(..., ...emptyArray) = -Infinity.
    if (pinCoords.length == 0) {
      if (!searchPoint) return

      this.boundsFitted = true
      this.map.setCenter(searchPoint)
      this.map.setZoom(NO_PINS_DEFAULT_ZOOM)
      return
    }

    // Center on the user; fall back to the first pin only when no search
    // point is available (rare: shared URL with filters but no address).
    // In the fallback case we still skip the trailing setCenter, because
    // setting the centroid of symmetric bounds twice would be redundant.
    const center = searchPoint || pinCoords[0]
    this.bounds = symmetricBoundsAround(center, pinCoords, MIN_BOUNDS_PADDING_DEG)

    this.boundsFitted = true
    this.map.fitBounds(this.bounds)

    // fitBounds applies internal padding (~45px) and rounds zoom to an
    // integer; both can drift the visual center by a few pixels off the
    // mathematical center of the bounds. setCenter forces pixel-perfect
    // alignment on the user's address.
    if (searchPoint) this.map.setCenter(searchPoint)
  }

  // The user's current delivery point — typed address on a fresh search,
  // or the new map center after a manual pan (the backend resolves them
  // to the same queryInfo data attributes). Returns null when no address
  // has been picked yet, in which case fitBounds has nothing to center on.
  searchPoint() {
    if (!this.hasQueryInfoTarget) return null

    const lat = parseFloat(this.queryInfoTarget.dataset.lat)
    const lng = parseFloat(this.queryInfoTarget.dataset.lng)
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null

    return { lat, lng }
  }

  trackClusterMapPin(targetMarkerInfoWindow) {
    const { catererId } = targetMarkerInfoWindow.dataset
    if (this.trackedMapMarkerPinsList.indexOf(catererId) == -1) {
      analytics.track("Caterer Search Map Pin", { caterer_id: catererId })
      this.trackedMapMarkerPinsList.push(catererId)
    }
  }

  addMapListeners() {
    super.addMapListeners()

    google.maps.event.addListener(this.map, "idle", () => {
      if (this.mapMovedByUser && !this.boundsFitted) {
        this.resetFormElementAddress()
        this.searchFromMap()
        this.mapMovedByUser = false

        if (!this.hasTrackedMapMoved) {
          analytics.track("Caterer Search Map Moved")
          this.hasTrackedMapMoved = true
        }
      } else {
        this.boundsFitted = false
      }
    })
  }

  addInfoWindow(marker, productInfoWindow) {
    marker.addListener("click", () => {
      const { catererId } = productInfoWindow.dataset
      if (this.trackedMapMarkerPinsList.indexOf(catererId) == -1) {
        analytics.track("Caterer Search Map Pin", { caterer_id: catererId })
        this.trackedMapMarkerPinsList.push(catererId)
      }
    })

    marker.addListener("click", () => {
      if (this.infoBox?.position_ == marker.position) {
        this.infoBox?.close()
        this.infoBox = null
      } else {
        const infoWindowContent = productInfoWindow.children[0].cloneNode(true)

        infoWindowContent.addEventListener("click", (e) => {
          this.trackClickedCaterer(e, { source: "map" })
        })

        if (this.infoBox) this.infoBox?.close()

        if (marker.infoBox) {
          this.infoBox = marker.infoBox
          this.infoBox.open(this.map, this.marker)
        } else {
          this.infoBox = new InfoBox({
            alignBottom: true,
            pixelOffset: this.infoBoxPixelOffset(),
            infoBoxClearance: new google.maps.Size(10, 50),
            visible: true,
            closeBoxURL: "",
            zIndex: 10000,
          })
          this.infoBox.setContent(infoWindowContent)
          this.infoBox.setPosition(marker.position)
          this.infoBox.open(this.map, this.marker)
          marker.infoBox = this.infoBox
        }

        this.productListItemTargets.forEach((e) => e.children[0].classList.remove("pinned"))
        productInfoWindow.previousElementSibling.children[0].classList.add("pinned")
        productInfoWindow.previousElementSibling.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }
    })
  }

  trackClickedCaterer(event, params = {}) {
    const isButtonClicked = event.target.closest("button")
    if (isButtonClicked) return

    const element = event.currentTarget.querySelector(".c--caterer-list-item, .c--caterer--info-window")
    if (!element) return

    const { catererId, position, page } = element.dataset
    const totalPosition = this.productPosition(position, page)
    const properties = {
      caterer_id: catererId,
      position: totalPosition,
      source: "list",
      ...params,
    }
    analytics.track("Search Result Clicked", properties)
  }

  trackVisibleCaterers() {
    this.productListItemTargets.forEach((card) => {
      const { catererId, position, page } = card.children[0].dataset
      if (
        this.isScrolledIntoView &&
        this.isScrolledIntoView(card) &&
        this.trackedProductList.indexOf(catererId) == -1
      ) {
        const totalPosition = this.productPosition(position, page)
        const properties = {
          caterer_id: catererId,
          position: totalPosition,
          page,
          source: "listing",
        }
        analytics.track("Viewed Search Result", properties)
        this.trackedProductList.push(catererId)
      }
    })
  }

  productPosition(position, page) {
    if (!position || !page) return
    return (page - 1) * PRODUCT_PER_PAGE + (Number(position) + 1)
  }
}
